import type { FlagRing, FlagStep } from 'definitions';
import type { AttributeRule } from 'types';
import type { Flag } from './flag-types';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { can } from 'definitions/capabilities';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { toast } from '@/components/ui/sonner';
import { apiErrorMessage } from '../api-error';
import { useOrganizations } from '../useOrganizations';
import { flagSummary, nextStep } from './flag-status';
import { FLAG_KEY, FLAG_POLL_MS, FLAGS_KEY } from './useFlags';

export type FlagAction =
  | 'rollout'
  | 'publish'
  | 'pause'
  | 'resume'
  | 'archive'
  | 'delete'
  | 'plan'
  | 'schedule'
  | 'eligibility'
  | 'rules'
  | 'goal';

export type PlanBody = { steps?: FlagStep[]; holdbackBp?: number; rings?: FlagRing[]; guardrailMarginBp?: number };

export type ScheduleBody = { publishAt?: number | null; offAt?: number | null };

/**
 * POST /flags/:id/rollout. expectedBp is the share the page showed when it was edited: the API
 * refuses the change (409) when the rollout has moved since. Turn off sends none.
 */
export type RolloutBody = { rolloutBp: number; expectedBp?: number };

/** The rollout engine's schedule columns, read only when the API sends them. */
export type FlagSchedule = { publishAt: number | null; offAt: number | null };

/**
 * One feature flag and everything that can be done to it. Each action posts to /flags/:id/…,
 * then refetches the flag and the list (after a failure too), and reports whether it went
 * through: a failed request has already been shown by useClient's error dialog, so the caller
 * only decides what stays open.
 */
/** Statuses useClient's error dialogs already show (ErrorDialogs): a toast on top would say it twice. */
const isShownByDialog = (error: unknown): boolean => {
  const status =
    error && typeof error === 'object' && 'status' in error ? Number((error as { status: unknown }).status) : 0;
  return status === 401 || status === 402 || status === 403 || status >= 500;
};

export const useFlag = (flagId: number) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { organizations, setOrganization, setWebsite } = useOrganizations();
  const [pending, setPending] = useState<FlagAction | null>(null);

  const {
    data: flag,
    isLoading,
    isError,
  } = useQuery<Flag>({
    queryKey: [FLAG_KEY, flagId],
    enabled: !!flagId,
    queryFn: () => client.get<Flag>(`/flags/${flagId}`),
    // The scheduler moves rollouts and publishes on schedule while the page is open. Every
    // card keeps its own draft beside the saved value (null until something is typed), so a
    // refetch moves what is saved without touching what is being edited.
    refetchInterval: FLAG_POLL_MS,
  });

  // A link from a notification or another account lands on the right scope, as an experiment does.
  useEffect(() => {
    if (!flag || !organizations) return;
    setOrganization(flag.website.company.id);
    setTimeout(() => setWebsite(flag.website.id));
  }, [flag?.id, organizations]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [FLAG_KEY, flagId] }),
      queryClient.invalidateQueries({ queryKey: [FLAGS_KEY] }),
    ]);
  };

  const run = async (action: FlagAction, request: () => Promise<unknown>): Promise<boolean> => {
    setPending(action);
    try {
      await request();
      await refresh();
      return true;
    } catch (error) {
      console.error(error);
      // useClient's dialogs cover 401, 402, 403 and 5xx only; the flag API's refusals are mostly
      // 400 and 409 (a stale rollout, steps changed under a running plan), which showed nothing.
      if (!isShownByDialog(error)) toast.error(apiErrorMessage(error, 'The change was not saved. Try again.'));
      // A refusal is often a race (409: the flag changed while it was edited), so the page
      // reloads what is saved rather than stay on what was refused.
      await refresh();
      return false;
    } finally {
      setPending(null);
    }
  };

  const setRollout = (body: RolloutBody) => run('rollout', () => client.post(`/flags/${flagId}/rollout`, body));
  const turnOff = () => setRollout({ rolloutBp: 0 });
  const publish = () => run('publish', () => client.post(`/flags/${flagId}/publish`, {}));
  const pausePlan = () => run('pause', () => client.post(`/flags/${flagId}/pause`, {}));
  const resumePlan = () => run('resume', () => client.post(`/flags/${flagId}/resume`, {}));
  const archive = () => run('archive', () => client.post(`/flags/${flagId}/archive`, {}));
  const savePlan = (body: PlanBody) => run('plan', () => client.put(`/flags/${flagId}/plan`, body));
  const saveSchedule = (body: ScheduleBody) => run('schedule', () => client.put(`/flags/${flagId}/schedule`, body));
  // A code flag's server rules: the API republishes a live flag's SDK config itself.
  const saveEligibility = (attributeTargeting: AttributeRule[]) =>
    run('eligibility', () => client.put(`/flags/${flagId}/eligibility`, { attributeTargeting }));
  // URL, cookie, device and activation targeting are shared with experiments, whose endpoints
  // do not republish, so a live flag's changes ship with this, as Enforce new rules does.
  const publishRules = () => run('rules', () => client.post(`/websites/${flag?.websiteId}/enforce-new-rules`, {}));
  // The primary goal, which the guardrail judges on, is an experiments column: PUT
  // /experiments/:id writes it for a flag too (refuseFlags does not cover it), and refuses a
  // change while the flag is live (objectivesLocked). null is the website's default.
  const savePrimaryGoal = (primaryGoal: string | null) =>
    run('goal', () => client.put(`/experiments/${flagId}`, { primaryGoal }));
  // The name is an experiments column too, and PUT /experiments/:id takes it for a flag at any status.
  // Throws on a refusal, so the rename dialog can stay open with what was typed.
  const rename = async (name: string) => {
    await client.put(`/experiments/${flagId}`, { name });
    await refresh();
  };
  // Deleting is shared with experiments (DELETE /experiments/:id), offered for drafts only.
  const remove = () => run('delete', () => client.delete(`/experiments/${flagId}`));

  const rollout = flag?.flagRollout ?? null;
  const schedule: FlagSchedule | null = rollout ? { publishAt: rollout.publishAt, offAt: rollout.offAt } : null;
  const isLive = flag?.status === 'Running';
  const canChange = can(user, 'experiments:start');
  const canEdit = can(user, 'experiments:create');
  const rolloutBp = rollout?.rolloutBp ?? 0;
  // Turn off is offered while something can still get On: a share above 0%, or a plan that widens
  // it on its next step (a plan at a 0% step).
  const offersTurnOff = isLive && (rolloutBp > 0 || rollout?.planStatus === 'active');

  return {
    flag,
    rollout,
    schedule,
    isLoading,
    isError,
    isDraft: flag?.status === 'Draft',
    isLive,
    isArchived: !!flag && flag.status !== 'Draft' && flag.status !== 'Running',
    isEditor: rollout?.kind === 'editor',
    canChange,
    canEdit,
    // Rings and server rules decide who gets On, so on a live flag they take the same right as
    // the rollout (experiments:start); on a draft, the right to edit it.
    canEditAudience: isLive ? canChange : canEdit,
    // The status card's stage, share, plan and pause, and what happens next (refetched every
    // FLAG_POLL_MS, so a wait counts down).
    summary: flag ? flagSummary(flag) : null,
    next: flag ? nextStep({ flag, now: Date.now() }) : null,
    pending,
    offersTurnOff,
    canTurnOff: offersTurnOff && canChange,
    refresh,
    rename,
    setRollout,
    turnOff,
    publish,
    pausePlan,
    resumePlan,
    archive,
    savePlan,
    saveSchedule,
    saveEligibility,
    publishRules,
    savePrimaryGoal,
    remove,
  };
};

export type FlagManager = ReturnType<typeof useFlag>;
